import { ChatListDocument } from 'src/app/common/models/chatbotexpand.model';
import { ChatHistoryHelper } from './chat-history.helper';

/**
 * ChatHistorySidebarHelper
 *
 * The two left-hand slide-out panels of the chatbotexpand component and the
 * chat-list data behind them:
 *
 *  - Chat History panel: open/close/back, accordion + month-group expansion,
 *    primary/nested search, loading the chat list and restoring the latest chat.
 *  - Saved (starred) Questions panel: open/close/back, loading starred messages,
 *    search, unstar, delete, and jumping to a starred question.
 *
 * Search tokenizing (`comp.tokenizeSearchText` / `comp.matchesAllTokens`) stays on
 * the component, as do downstream actions such as `comp.getChatResponse`,
 * `comp.navigateToLanding`, `comp.showToast`, `comp.scrollToBottom` and
 * `comp.clearOutputDataObject`.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged.
 */
export class ChatHistorySidebarHelper {

  // ---------------------------------------------------------------------------
  // Panel open / close
  // ---------------------------------------------------------------------------

  static toggleChatHistory(comp: any): void {
    if (comp.isSavedQuestionsOpen) {
      comp.isSavedQuestionsOpen = false;
    }
    comp.isChatHistoryOpen = !comp.isChatHistoryOpen;
  }

  static toggleSavedSelections(comp: any): void {
    if (comp.isChatHistoryOpen) {
      comp.isChatHistoryOpen = false;
    }
    comp.isSavedQuestionsOpen = !comp.isSavedQuestionsOpen;

    if (comp.isSavedQuestionsOpen) {
      comp.loadStarredMessages();
    }
  }

  private static closeChatHistory(comp: any): void {
    comp.isChatHistoryOpen = false;
  }

  private static closeSavedQuestions(comp: any): void {
    comp.isSavedQuestionsOpen = false;
  }

  static onCloseChatHistory(comp: any): void {
    comp.isClosing = true;
    setTimeout(() => {
      ChatHistorySidebarHelper.closeChatHistory(comp);
      comp.isClosing = false;
    }, 300);
  }

  static onBackClickChatHistory(comp: any): void {
    comp.onCloseChatHistory();
  }

  static onCloseSavedQuestions(comp: any): void {
    comp.isClosing = true;
    setTimeout(() => {
      ChatHistorySidebarHelper.closeSavedQuestions(comp);
      comp.isClosing = false;
    }, 300);
  }

  static onBackClickSavedQuestions(comp: any): void {
    comp.onCloseSavedQuestions();
  }

  // ---------------------------------------------------------------------------
  // Chat history accordion
  // ---------------------------------------------------------------------------

  static toggleAccordion(comp: any, item: any): void {
    if (item.isExpanded) {
      item.isExpanded = false;
      item.isCollapsing = true;
      clearTimeout(item.collapseTimer);
      item.collapseTimer = setTimeout(() => {
        item.isCollapsing = false;
      }, 550);
      return;
    }

    clearTimeout(item.collapseTimer);
    item.isCollapsing = false;
    item.isExpanded = true;
  }

  static toggleMonthGroup(comp: any, group: any): void {
    group.isExpanded = !group.isExpanded;
    if (!group.isExpanded && group.chats) {
      group.chats.forEach((chat: any) => {
        chat.isExpanded = false;
        chat.isCollapsing = false;
        clearTimeout(chat.collapseTimer);
      });
    }
  }

  static onChatHeaderQuestionClick(comp: any, item: any): void {
    comp.isChatHistoryOpen = false;
    if (item.mainQuestionMessageId) {
      comp.getChatResponse(item.mainQuestionMessageId);
    }
  }

  // ---------------------------------------------------------------------------
  // Chat list data + search
  // ---------------------------------------------------------------------------

  static getChatListData(comp: any) {
    comp.apiService.getChatList(comp.selected_client, comp.selected_study).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData && apiResponse.Data) {
          const allChats = JSON.parse(apiResponse.Data) as ChatListDocument[];
          comp.allChatList = allChats;
          comp.onChatSearch();

        } else {
          comp.recentChats = [];
          comp.olderChats = [];
          comp.allChatList = [];
          comp.chatAccordions = [];
        }
        comp.isChatListLoaded = true;
        if (typeof comp.checkHistoryReady === 'function') {
          comp.checkHistoryReady();
        }
      },
      error: () => {
        comp.recentChats = [];
        comp.olderChats = [];
        comp.isChatListLoaded = true;
        if (typeof comp.checkHistoryReady === 'function') {
          comp.checkHistoryReady();
        }
      }
    });
  }

  static loadLatestChatIfExists(comp: any) {
    if (comp.isNewChatSession) {
      comp.isNewChatSession = false;
      comp.currentView = 'landing';
      comp.arktabaccess = false;
      return;
    }

    setTimeout(() => {
      if (comp.allChatList && comp.allChatList.length > 0) {
        const sortedChats = ChatHistoryHelper.sortChatsByDate(comp.allChatList);
        const latestChat = sortedChats[0];

        if (latestChat && latestChat.ChatId) {
          comp.newChatId = latestChat.ChatId;
          comp.guidChatId = latestChat.ChatId;
          comp.selectedChatId = latestChat.ChatId;

          if (latestChat.ChatHistory && latestChat.ChatHistory.length > 0) {
            comp.currentView = 'chat';
            const sortedHistory = ChatHistoryHelper.sortChatHistoryByIndex(latestChat.ChatHistory);
            comp.chatPreviewBlocks = sortedHistory.map((msg: any) =>
              ChatHistoryHelper.createChatPreviewBlock(msg, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
            );
            comp.scrollToBottom();
            const lastMessage = sortedHistory[sortedHistory.length - 1];
            if (lastMessage && lastMessage.MessageId) {
              comp.getChatResponse(lastMessage.MessageId, true);
            }
            comp.arktabaccess = false;
          } else {
            comp.currentView = 'landing';
            comp.arktabaccess = false;
          }
        } else {
          comp.currentView = 'landing';
          comp.arktabaccess = false;
        }
      } else {
        comp.currentView = 'landing';
        comp.arktabaccess = false;
      }
    }, 1000);
  }

  static onChatSearch(comp: any): void {
    const searchText = comp.chatSearchText.toLowerCase().trim();
    const nestedSearchText = comp.isNestedSearchVisible ? comp.chatNestedSearchText.toLowerCase().trim() : '';

    if (comp.allChatList.length === 0 && (comp.recentChats.length > 0 || comp.olderChats.length > 0)) {
      comp.allChatList = [...comp.recentChats, ...comp.olderChats];
    }

    // Always calculate primary count for conditional toggle visibility
    const primaryOnly = ChatHistoryHelper.filterChatsBySearch(comp.allChatList, searchText, '');
    comp.primaryFilteredCount = primaryOnly.length;

    const filteredChats = ChatHistoryHelper.filterChatsBySearch(comp.allChatList, searchText, nestedSearchText);
    comp.chatAccordions = ChatHistoryHelper.mapChatsToAccordions(filteredChats);
  }

  static toggleNestedSearch(comp: any): void {
    comp.isNestedSearchVisible = !comp.isNestedSearchVisible;
    if (!comp.isNestedSearchVisible) {
      comp.chatNestedSearchText = '';
    }
    comp.onChatSearch();
  }

  // ---------------------------------------------------------------------------
  // Saved (starred) questions
  // ---------------------------------------------------------------------------

  static loadStarredMessages(comp: any): void {
    comp.isLoadingStarredMessages = true;
    comp.apiService.getStarredMessages(comp.selected_client, comp.selected_study).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData && apiResponse.Data) {
          const parsedData = JSON.parse(apiResponse.Data);
          comp.starredMessages = parsedData.map((msg: any) => {
            let answer = msg.QuestionAnswer;
            if (!answer) {
              const chat = comp.allChatList.find((c: any) => c.ChatId === msg.ChatId);
              if (chat && chat.ChatHistory) {
                const historyItem = chat.ChatHistory.find((h: any) => h.MessageId === msg.MessageId);
                if (historyItem) {
                  answer = historyItem.QuestionAnswer;
                }
              }
            }
            return {
              ...msg,
              QuestionAnswer: answer,
              AnswerPreview: ChatHistoryHelper.createAnswerPreview(answer)
            };
          });
          comp.filteredStarredMessages = [...comp.starredMessages];
        } else {
          comp.starredMessages = [];
          comp.filteredStarredMessages = [];
        }
        comp.isLoadingStarredMessages = false;
      },
      error: (err: any) => {
        comp.starredMessages = [];
        comp.filteredStarredMessages = [];
        comp.isLoadingStarredMessages = false;
      }
    });
  }

  static onStarredSearch(comp: any): void {
    const searchText = comp.starredSearchText.toLowerCase().trim();
    if (!searchText) {
      comp.filteredStarredMessages = [...comp.starredMessages];
      return;
    }

    const searchTokens = comp.tokenizeSearchText(searchText.toLowerCase());
    comp.filteredStarredMessages = comp.starredMessages.filter((msg: any) =>
      comp.matchesAllTokens(msg.Question || '', searchTokens)
    );
  }

  static unstarFromSaved(comp: any, message: any, event: Event): void {
    event.stopPropagation();
    const messageId = message.MessageId || message.messageId || message.id;
    if (!messageId) {
      console.warn('[unstarFromSaved] ❌ No message ID found in:', message);
      return;
    }

    comp.apiService.updateChatStarred(messageId, false).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData) {
          // Update local starredMessages list
          const beforeCount = comp.starredMessages.length;
          comp.starredMessages = comp.starredMessages.filter((m: any) => {
            const mId = (m.MessageId || (m as any).messageId || '').toString().toLowerCase();
            const targetId = (messageId || '').toString().toLowerCase();
            return mId !== targetId;
          });
          comp.onStarredSearch(); // Refresh filtered list

          // Also update allChatList if it contains this message
          comp.allChatList.forEach((chat: any) => {
            const msg = chat.ChatHistory?.find((h: any) => {
              const hId = (h.MessageId || h.messageId || '').toString().toLowerCase();
              const targetId = (messageId || '').toString().toLowerCase();
              return hId === targetId;
            });
            if (msg) {
              msg.IsStarred = false;
              (msg as any).isStarred = false;
            }
          });

          // Update chatPreviewBlocks if current
          const currentMsg = comp.chatPreviewBlocks.find((b: any) => {
            const bId = (b.messageId || '').toString().toLowerCase();
            const targetId = (messageId || '').toString().toLowerCase();
            return bId === targetId;
          });
          if (currentMsg) {
            currentMsg.isStarred = false;
          }

          comp.showToast('Removed from saved questions', 'success');
        }
      }
    });
  }

  static deleteFromSaved(comp: any, message: any, event: Event): void {
    event.stopPropagation();
    const messageId = message.MessageId || message.messageId || message.id;
    if (!messageId) return;

    if (!confirm('Are you sure you want to delete this question?')) return;

    comp.apiService.updateChatStarred(messageId, false).subscribe({
      next: () => {
        comp.apiService.deleteChatMessage(messageId).subscribe({
          next: (res: string) => {
            const apiResponse = JSON.parse(res);
            if (apiResponse.HasValidData) {
              // Remove from local lists
              const beforeCount = comp.starredMessages.length;
              comp.starredMessages = comp.starredMessages.filter((m: any) => {
                const mId = (m.MessageId || (m as any).messageId || '').toString().toLowerCase();
                const targetId = (messageId || '').toString().toLowerCase();
                return mId !== targetId;
              });
              comp.onStarredSearch();

              // Update allChatList
              comp.allChatList.forEach((chat: any) => {
                if (chat.ChatHistory) {
                  const idx = chat.ChatHistory.findIndex((h: any) => {
                    const hId = (h.MessageId || h.messageId || '').toString().toLowerCase();
                    const targetId = (messageId || '').toString().toLowerCase();
                    return hId === targetId;
                  });
                  if (idx > -1) {
                    chat.ChatHistory.splice(idx, 1);
                  }
                }
              });

              // Remove from chatPreviewBlocks if it exists
              const chatObj = comp.chatPreviewBlocks.find((b: any) => {
                const bId = (b.messageId || '').toString().toLowerCase();
                const targetId = (messageId || '').toString().toLowerCase();
                return bId === targetId;
              });

              if (chatObj) {
                const pIndex = comp.chatPreviewBlocks.indexOf(chatObj);
                if (pIndex > -1) comp.chatPreviewBlocks.splice(pIndex, 1);
              }

              // Handle current view if deleting the active message or if list is empty
              if (messageId === comp.selectedMessageId || comp.chatPreviewBlocks.length === 0) {
                comp.selectedMessageId = null;
                comp.displayTable = false;
                comp.showSnapshot = false;
                comp.clearOutputDataObject();
                comp.isInsightspopupOpen = false;

                if (comp.chatPreviewBlocks.length === 0) {
                  comp.navigateToLanding();
                }
              }

              comp.showToast('Question deleted successfully', 'success');
            }
          }
        });
      },
      error: () => {
        console.warn('[deleteFromSaved] ⚠️ Pre-emptive unstar failed, proceeding with delete anyway');
        // Fallback to just delete if unstar fails
        comp.apiService.deleteChatMessage(messageId).subscribe();
      }
    });
  }

  static onStarredQuestionClick(comp: any, message: { MessageId: string; Question: string; ChatId: string }): void {
    comp.isSavedQuestionsOpen = false;
    const chat = comp.allChatList.find((c: any) => c.ChatHistory && c.ChatHistory.some((h: any) => h.MessageId === message.MessageId));

    if (chat) {
      const specificMsg = chat.ChatHistory?.find((h: any) => h.MessageId === message.MessageId);

      if (specificMsg) {
        comp.chatPreviewBlocks = [
          ChatHistoryHelper.createChatPreviewBlock(specificMsg, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
        ];

        comp.newChatId = chat.ChatId || null;
        comp.guidChatId = chat.ChatId || '';
        comp.selectedChatId = chat.ChatId || null;
        comp.currentView = 'chat';
      }
    } else {
      // Fallback if chat is not in current list
      comp.chatPreviewBlocks = [
        ChatHistoryHelper.createChatPreviewBlock({
          Question: message.Question || '',
          MessageId: message.MessageId,
          IsStarred: true
        }, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
      ];
      comp.guidChatId = message.ChatId || message.MessageId;
      comp.selectedChatId = message.ChatId || message.MessageId;
      comp.currentView = 'chat';
    }

    comp.selectedMessageId = message.MessageId;
    comp.getChatResponse(message.MessageId, true);
  }
}
